import type { AskAiAction } from './ask-ai-utils';

import { useState } from 'react';
import { Iconify } from '@classroom-ai/ui/components/iconify';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import CircularProgress from '@mui/material/CircularProgress';

import { PERMISSION_DENIED_MESSAGE } from 'src/auth/use-permissions';

// ----------------------------------------------------------------------

type Props = {
  action: AskAiAction;
  /** lesson:manage + lesson:generate_ai — advisory; the server enforces it. */
  canRun: boolean;
  /** Queue the action (a background job); progress arrives via `action.running` / `outcome`. */
  onRun: (replace: boolean) => Promise<void>;
  onRevert: () => Promise<void>;
  onOpenEditor: (href: string) => void;
};

const WHAT: Record<AskAiAction['type'], string> = {
  create_practice_block: 'write an AI Practice Block for',
  create_exit_quiz: 'write an Exit Ticket quiz (only answer keys it can verify are added) for',
  draft_lesson_script: 'draft and narrate a Teach with AI lesson for',
  generate_unit_assessment: 'generate a Pre/Post Unit Assessment for',
  // "Clear with Beacon" plan steps (epic #575) never appear as chat cards, but share the type.
  grade_review: 'grade a pending answer for',
  acknowledge_alert: 'acknowledge an alert for',
  assign_makeup: 'assign a make-up lesson to',
  extend_makeup: 'move the due date of a make-up for',
  assign_focus_practice: 'assign focus practice to',
  extend_practice: 'move the due date of focus practice for',
};

/**
 * One proposed fix from Ask AI (epic #391): **Do it** asks for confirmation, then runs it
 * on the server in the background; **Open in editor** goes to the lesson with that editor
 * open. Existing content is never replaced without an explicit "Replace" confirmation, and
 * a finished change can be reverted while the lesson is unedited (Phase G).
 */
export function AskAiActionCard({ action, canRun, onRun, onRevert, onOpenEditor }: Props) {
  const [step, setStep] = useState<'idle' | 'confirm' | 'confirm-revert' | 'reverting'>('idle');
  const replacing = action.existing && action.canReplace;
  const blocked = action.existing && !action.canReplace;
  const outcome = action.outcome;
  const done = outcome?.status === 'created';
  const reverted = !!action.revertedMessage;
  const running = !!action.running;

  const confirmRun = async () => {
    setStep('idle');
    await onRun(replacing);
  };
  const confirmRevert = async () => {
    setStep('reverting');
    try {
      await onRevert();
    } finally {
      setStep('idle');
    }
  };

  return (
    <Card variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Iconify icon="solar:atom-bold-duotone" width={20} sx={{ color: 'primary.main', mt: 0.25 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2">{action.label}</Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {action.targetTitle}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {action.reason}
          </Typography>
        </Box>
      </Box>

      {running && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, color: 'text.secondary' }}>
          <CircularProgress size={14} />
          <Typography variant="caption">Working in the background — you can close this; it keeps going.</Typography>
        </Box>
      )}
      {!running && outcome && !reverted && (
        <Alert severity={done ? 'success' : outcome.status === 'skipped' ? 'warning' : 'info'} sx={{ mt: 1.5 }}>
          {outcome.message}
        </Alert>
      )}
      {reverted && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          {action.revertedMessage}
        </Alert>
      )}
      {action.failed && !running && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {action.failed}
        </Alert>
      )}

      {step === 'confirm' && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {replacing
              ? `This replaces the existing content of "${action.targetTitle}" — any edits to it are lost (you can revert right after). Replace it?`
              : `AI will ${WHAT[action.type]} "${action.targetTitle}" and save it. You can edit it afterwards.`}
          </Typography>
          {action.warning && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {action.warning}
            </Alert>
          )}
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" color={replacing ? 'warning' : 'primary'} onClick={confirmRun}>
              {replacing ? 'Replace' : 'Confirm'}
            </Button>
            <Button size="small" onClick={() => setStep('idle')}>
              Cancel
            </Button>
          </Stack>
        </Box>
      )}
      {step === 'confirm-revert' && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Undo this change and restore &ldquo;{action.targetTitle}&rdquo; to how it was before?
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" color="warning" onClick={confirmRevert}>
              Revert
            </Button>
            <Button size="small" onClick={() => setStep('idle')}>
              Cancel
            </Button>
          </Stack>
        </Box>
      )}

      {(step === 'idle' || step === 'reverting') && (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5, alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
          {done && !reverted ? (
            <>
              <Button size="small" variant="contained" onClick={() => onOpenEditor(outcome.link)}>
                View
              </Button>
              {outcome.revertible && canRun && (
                <LoadingButton size="small" color="warning" loading={step === 'reverting'} onClick={() => setStep('confirm-revert')}>
                  Revert
                </LoadingButton>
              )}
            </>
          ) : action.runnable && !blocked && !running ? (
            <Tooltip title={canRun ? '' : PERMISSION_DENIED_MESSAGE}>
              <span>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!canRun}
                  color={replacing ? 'warning' : 'primary'}
                  onClick={() => setStep('confirm')}
                >
                  {replacing ? 'Replace…' : reverted ? 'Do it again' : 'Do it'}
                </Button>
              </span>
            </Tooltip>
          ) : null}
          <Button size="small" variant="outlined" onClick={() => onOpenEditor(action.editorLink)}>
            Open in editor
          </Button>
          {blocked && !done && (
            <Typography variant="caption" color="text.secondary">
              Already exists — open it to review.
            </Typography>
          )}
        </Stack>
      )}
    </Card>
  );
}

// ----------------------------------------------------------------------

/** "Do all": one confirmation runs every pending card of an answer, in dependency order. */
export function AskAiDoAll({ actions, onRun }: { actions: AskAiAction[]; onRun: (actions: AskAiAction[]) => Promise<void> }) {
  const [confirming, setConfirming] = useState(false);
  const warnings = actions.filter((a) => a.warning && a.type !== 'create_exit_quiz');

  if (!confirming) {
    return (
      <Box>
        <Button size="small" variant="soft" color="primary" onClick={() => setConfirming(true)}>
          Do all ({actions.length})
        </Button>
      </Box>
    );
  }
  return (
    <Alert severity="info" icon={false}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        AI will run these {actions.length} in the background (practice blocks before their quizzes) and save each:
      </Typography>
      <Box component="ul" sx={{ pl: 2.5, my: 0.5 }}>
        {actions.map((a) => (
          <li key={a.actionId}>
            <Typography variant="body2" component="span">
              {a.label} — {a.targetTitle}
            </Typography>
          </li>
        ))}
      </Box>
      {warnings.map((a) => (
        <Typography key={a.actionId} variant="caption" color="warning.main" component="div">
          {a.warning}
        </Typography>
      ))}
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button
          size="small"
          variant="contained"
          onClick={async () => {
            setConfirming(false);
            await onRun(actions);
          }}
        >
          Run all
        </Button>
        <Button size="small" onClick={() => setConfirming(false)}>
          Cancel
        </Button>
      </Stack>
    </Alert>
  );
}
